import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { company, DEFAULT_COMPANIES } from '~/company/Company';
import Counter from '~/components/Counter';
import Icon from '~/components/Icon/Icon';
import { useGlobal } from '~/hooks/useGlobal';
import { useNotifyMe } from '~/hooks/useNotifyMe';
import { Offer, Product } from '~/stores/CategoriesStore';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

import styles from '../Product.module.scss';

import ProductPrice from './ProductPrice';

type Props = {
  offer?: Offer;
  productState?: Product | null;

  count: number;
  isMultiBuy: boolean;
  oldPrice: string | null;
  currentPrice?: string;
  isExpress?: boolean | 0;
  showFlashIcon: boolean;
  expressDeliveryLabel: string;
  expressDeliveryLabelArgs: Record<string, string>;
  isBackorderAvailable?: boolean;
  deliveryToday: boolean | '';
  backorderDate: string | null;
  limit: number;
  isBundle: boolean;

  handleChangeCount: (count: number, action: 'add' | 'remove') => void;
  handleFavorite: () => void;
  handleCopyCode: () => Promise<void>;
};

const ProductPriceInfoLocalee = ({
  offer,
  productState,

  count,
  isMultiBuy,
  oldPrice,
  currentPrice,
  isExpress,
  expressDeliveryLabel,
  expressDeliveryLabelArgs,
  isBackorderAvailable,
  deliveryToday,
  backorderDate,
  limit,
  isBundle,

  handleChangeCount,
  handleFavorite,
  handleCopyCode,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();

  const { notifyMeOnClick, notifyMeIcon, notifyMeButtonText, btnClassName } =
    useNotifyMe(productState?.id || 0, offer?.sku || '', isBundle);

  const elementView = () => {
    const ctaButtonComponent =
      limit > 0 ? (
        count > 0 ? (
          <div className="counter-container">
            {!isMobile && (
              <>
                <Icon
                  type="check-round"
                  size={36}
                  className="counter-container__icon"
                />
                <p className="counter-container__text">{t('addedToCart')}</p>
              </>
            )}
            <Counter
              size="m"
              count={count}
              onClick={handleChangeCount}
              limit={limit}
            />
          </div>
        ) : (
          <button
            data-button="add-to-cart"
            className={classes(['button _primary _m price-info-buttons__cta'])}
            onClick={() => handleChangeCount(count + 1, 'add')}
          >
            {t('addToCart')}
          </button>
        )
      ) : (
        <button
          className={classes([
            'button _bordered _m price-info-buttons__cta',
            btnClassName,
          ])}
          onClick={notifyMeOnClick}
        >
          <Icon type={notifyMeIcon} size={24} />
          {notifyMeButtonText}
        </button>
      );

    if (DEFAULT_COMPANIES.includes(company.name)) {
      return (
        <div className="price-info" data-company={company.name}>
          <div className="price-info-buttons">
            {ctaButtonComponent}
            <button
              data-button="like"
              className="button _med price-info-buttons__actions-btn"
              onClick={handleFavorite}
            >
              <Icon
                size={24}
                type={
                  favoritesStore.isFavorited(productState?.id)
                    ? 'fav-fill'
                    : 'fav'
                }
                className={classes(
                  favoritesStore.isFavorited(productState?.id) && '_fill',
                )}
              />
            </button>
            <button
              data-button="share"
              className="button _med price-info-buttons__actions-btn"
              onClick={handleCopyCode}
            >
              <Icon size={24} type="share" />
            </button>
          </div>
          <div className="price-info__bages">
            {isExpress ? (
              <div className="badge__container">
                {t(expressDeliveryLabel, expressDeliveryLabelArgs)}
              </div>
            ) : isBackorderAvailable ? (
              <div className={styles.backorder_date}>
                <Icon
                  type="calendar-2"
                  size={20}
                  className={styles.backorder_date__icon}
                />
                {deliveryToday
                  ? t('deliveryToday')
                  : t('slotDeliveryFrom', { backorderDate })}
              </div>
            ) : null}
          </div>
        </div>
      );
    } else {
      return (
        <div className="price-info" data-company={company.name}>
          <div className="price-info-container">
            <ProductPrice
              offer={offer}
              isMultiBuy={isMultiBuy}
              oldPrice={oldPrice}
              currentPrice={currentPrice}
            />
            {limit > 0 && !!isExpress && (
              <p className="price-info__status-in-stock">{t('inStock')}</p>
            )}
          </div>
          <div className="price-info-buttons">
            {count > 0 ? (
              <div className="counter-container">
                <Icon
                  type="check-round"
                  size={36}
                  className="counter-container__icon"
                />
                <p className="counter-container__text">{t('addedToCart')}</p>
                <Counter
                  size="m"
                  count={count}
                  onClick={handleChangeCount}
                  limit={limit}
                />
              </div>
            ) : (
              <button
                data-button="add-to-cart"
                className={classes([
                  'button _primary _med price-info-buttons__cta',
                  limit === 0 && '_disabled',
                ])}
                onClick={() => handleChangeCount(count + 1, 'add')}
              >
                {limit > 0 ? t('addToCart') : t('soldOut')}
              </button>
            )}
            <button
              data-button="like"
              className="button _med price-info-buttons__actions-btn"
              onClick={handleFavorite}
            >
              <Icon
                size={24}
                type={
                  favoritesStore.isFavorited(productState?.id)
                    ? 'fav-fill'
                    : 'fav'
                }
                className={classes(
                  favoritesStore.isFavorited(productState?.id) && '_fill',
                )}
              />
            </button>
            <button
              data-button="share"
              className="button _med price-info-buttons__actions-btn"
              onClick={handleCopyCode}
            >
              <Icon size={24} type="share" />
            </button>
          </div>
          <div className="price-info__bages">
            {isExpress ? (
              <div className="badge__container">
                {t(expressDeliveryLabel, expressDeliveryLabelArgs)}
              </div>
            ) : isBackorderAvailable ? (
              <div className={styles.backorder_date}>
                <Icon
                  type="calendar-2"
                  size={20}
                  className={styles.backorder_date__icon}
                />
                {deliveryToday
                  ? t('deliveryToday')
                  : t('slotDeliveryFrom', { backorderDate })}
              </div>
            ) : null}
          </div>
        </div>
      );
    }
  };

  return offer ? (
    elementView()
  ) : (
    <div className="price-info _skeleton">
      <Skeleton className="skeleton" containerClassName="skeleton-container" />
    </div>
  );
};

export default observer(ProductPriceInfoLocalee);
